import Link from "next/link";
import { useRouter } from "next/router";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

import classNames from "classnames";
import { twMerge } from "tailwind-merge";

import { faExclamationCircle } from "@fortawesome/free-solid-svg-icons/faExclamationCircle";

import FontAwesomeIcon from "components/FontAwesomeIcon";
import useActivityTracker from "hooks/useActivityTracker";
import useAuthentication from "hooks/useAuthentication";
import useMediaQuery from "hooks/useMediaQuery";

import styles from "./style.module.scss";

type EntityPageTabsProps = {
  slug: string;
  entity: any;
};

const EntityPageTabs = ({ slug, entity }: EntityPageTabsProps) => {
  const router = useRouter();
  const { mobile } = useMediaQuery();
  const { trackActivity, trackEvent } = useActivityTracker();
  const { account: loggedInAccount, managedEntity } = useAuthentication();

  const tabs = useMemo(
    () => [
      { name: "Research", href: `/entities/${slug}/research` },
      ...(!loggedInAccount?.isCorporateOfficer ||
      managedEntity?.id === entity.id
        ? [
            {
              name: "Discussions",
              noFollow: true,
              href: `/entities/${slug}/discussions`,
            },
          ]
        : []),
      { name: "Announcements", href: `/entities/${slug}/announcements` },
      ...(!mobile
        ? [
            {
              name: "Analytics",
              noFollow: true,
              href: `/entities/${slug}/analytics`,
            },
          ]
        : []),
      ...(loggedInAccount &&
      !loggedInAccount.isPreviewPass &&
      loggedInAccount.canViewInsightThemes
        ? [
            {
              name: "Themes",
              customName: (
                <div className="flex">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src="https://assets.smartkarma.com/icons/sparkle.svg"
                    alt="Quiddity Northbound Monitor"
                    width={mobile ? 15 : 20}
                    height={mobile ? 14 : 22}
                    className="pb-[2px] md:absolute md:left-[-2px]"
                  />
                  Themes
                  <OverlayTrigger
                    overlay={
                      <Tooltip>
                        Themes are AI-extracted nuggets of information,
                        organized into topics of interest based on premium
                        insights from Smartkarma. Each theme represents a
                        concise, valuable piece of information from
                        comprehensive analysis, giving users a dynamic
                        understanding of evolving market trends.
                      </Tooltip>
                    }
                  >
                    <span className="">
                      <FontAwesomeIcon
                        className="ml-[2px] md:ml-[10px] pb-[4px] md:pb-[1px] text-[15px] text-gray-gray"
                        icon={faExclamationCircle}
                      />
                    </span>
                  </OverlayTrigger>
                </div>
              ),
              href: `/entities/${slug}/themes`,
              className: "md:!pl-[40px] ml-[-20px]",
            },
          ]
        : []),
      { name: "News", href: `/entities/${slug}/news` },
    ],
    [loggedInAccount, mobile, slug]
  );

  const scrollableRef = useRef<HTMLDivElement>(null);

  const scrollToTab = useCallback((tab) => {
    const scrollable = scrollableRef.current;

    const scrollLeft =
      tab.offsetLeft -
      (scrollable?.offsetLeft || 0) +
      tab.offsetWidth -
      (scrollable?.scrollWidth || 0) / 2;

    scrollable?.scroll({
      left: scrollLeft,
      behavior: "smooth",
    });
  }, []);

  const onTabClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>, tab: string) => {
      try {
        const tabElement = event.currentTarget;
        scrollToTab(tabElement);
      } catch {}

      try {
        trackActivity("entity.view", entity, { tab });
      } catch (error) {
        console.debug(error);
      }

      try {
        trackEvent("Tab - Click", {
          event_category: "Tab",
          event_action: "Click",
          event_label: tab,
        });
      } catch (error) {
        console.debug(error);
      }
    },
    [entity, scrollToTab, trackActivity, trackEvent]
  );

  const activeTab = useRef<HTMLSpanElement | null>(null);
  const onTabLoad = useCallback(
    (ref, tab) => {
      if (
        !!ref &&
        (router.asPath === tab.href || router.asPath.startsWith(`${tab.href}?`))
      ) {
        activeTab.current = ref;
      }
    },
    [router.asPath]
  );
  useEffect(() => {
    if (activeTab.current) scrollToTab(activeTab.current);
  }, [scrollToTab]);

  const [stickyTop, setStickyTop] = useState(false);
  const [top, setTop] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!mobile) return;

    const container = containerRef.current;

    const updateTabsPosition = () => {
      const top =
        container?.getBoundingClientRect()?.top || Number.MAX_SAFE_INTEGER;
      setTimeout(() => {
        setStickyTop(top < 0);
        setTop(top < 0 ? 0 : top);
      }, 100);
    };

    updateTabsPosition();

    window.addEventListener("scroll", updateTabsPosition, { passive: true });

    return () => {
      window.removeEventListener("scroll", updateTabsPosition);
    };
  }, [mobile]);

  return (
    <>
      <div
        className={classNames(
          styles.placeholder,
          mobile && styles.mobileLayout,
          stickyTop && styles.stickyTabs
        )}
        ref={containerRef}
      />
      <div
        className={classNames(
          styles.entityPageTabs,
          mobile && styles.mobileLayout,
          stickyTop && styles.stickyTabs,
          "skiptranslate"
        )}
        style={stickyTop ? { top } : {}}
      >
        <div className={styles.tabs} ref={scrollableRef}>
          {tabs.map((tab: any) => {
            return (
              <Link
                href={tab.href}
                key={tab.name}
                rel={tab.noFollow && "nofollow"}
                prefetch={false}
                scroll={!mobile}
                onClick={(e) => onTabClick(e, tab.name)}
                className={twMerge(
                  "!text-[11px] xs:!text-[12px] md:!text-[18px]",
                  (router.asPath === tab.href ||
                    router.asPath.startsWith(`${tab.href}?`)) &&
                    styles.activeTab,
                  [`/entities/${slug}/announcements`].includes(tab.href) &&
                    "!min-w-[130px]",
                  ![`/entities/${slug}/announcements`].includes(tab.href) &&
                    "flex-1 !px-[10px] xs:!px-[20px]",
                  tab.className
                )}
                ref={(ref) => onTabLoad(ref, tab)}
              >
                {tab.customName || tab.name}
              </Link>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default EntityPageTabs;
